import { useEffect, useState } from 'react';
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  Building2,
  Check,
  ChevronRight,
  House,
  Menu,
  Moon,
  ShieldCheck,
  Sun,
  X
} from 'lucide-react';

const SITE_THEME_KEY = 'altios-site-theme';

type Theme = 'dark' | 'light';

function setDocumentTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  window.localStorage.setItem(SITE_THEME_KEY, theme);
}

function ThemeToggle({
  theme,
  onToggle
}: {
  theme: Theme;
  onToggle: () => void;
}) {
  return (
    <button
      className="site-theme-toggle"
      type="button"
      onClick={onToggle}
      aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
    </button>
  );
}

export function App() {
  const [theme, setTheme] = useState<Theme>(() =>
    window.localStorage.getItem(SITE_THEME_KEY) === 'light'
      ? 'light'
      : 'dark'
  );

  const [menuOpen, setMenuOpen] = useState(false);
  const [showReveal, setShowReveal] = useState(
    () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (reducedMotion.matches) {
      setShowReveal(false);
      return;
    }

    const timer = window.setTimeout(() => setShowReveal(false), 3000);

    const onChange = () => {
      if (reducedMotion.matches) setShowReveal(false);
    };

    reducedMotion.addEventListener('change', onChange);

    return () => {
      window.clearTimeout(timer);
      reducedMotion.removeEventListener('change', onChange);
    };
  }, []);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setDocumentTheme(next);
  };

  return (
    <div className="site">
      {showReveal ? (
        <section className="site-reveal" aria-label="Altios Builders">
          <div className="site-reveal__field" aria-hidden="true">
            <span className="site-reveal__line site-reveal__line--vertical" />
            <span className="site-reveal__line site-reveal__line--horizontal" />
            <span className="site-reveal__corner site-reveal__corner--one" />
            <span className="site-reveal__corner site-reveal__corner--two" />
            <span className="site-reveal__glow" />
          </div>

          <div className="site-reveal__mark">
            <div className="site-reveal__logo">
              <img
                src="/logo.png"
                width="600"
                height="300"
                alt="Altios Builders"
              />
              <span className="site-reveal__sheen" aria-hidden="true" />
              <span className="site-reveal__spark site-reveal__spark--a" aria-hidden="true" />
              <span className="site-reveal__spark site-reveal__spark--b" aria-hidden="true" />
              <span className="site-reveal__spark site-reveal__spark--c" aria-hidden="true" />
            </div>

            <p>Crafting houses into homes.</p>
          </div>

          <button type="button" onClick={() => setShowReveal(false)}>
            Enter Altios
            <ArrowRight size={16} />
          </button>
        </section>
      ) : null}

      <header className="site-header">
        <a className="site-brand" href="#top" aria-label="Altios Builders home">
          <img
            src="/logo.png"
            width="600"
            height="300"
            alt="Altios Builders"
          />
        </a>

        <nav className="site-nav" aria-label="Main navigation">
          <a href="#philosophy">Why Altios</a>
          <a href="#services">What we build</a>
          <a href="#process">How we work</a>
          <a href="#work">Our work</a>
        </nav>

        <div className="site-header__actions">
          <ThemeToggle theme={theme} onToggle={toggleTheme} />

          <a
            className="site-header__portal"
            href="https://portal.altiosbuilders.com"
          >
            Customer Portal
          </a>

          <a
            className="site-header__cta"
            href="https://intake.altiosbuilders.com"
          >
            Start a project
            <ArrowUpRight size={15} />
          </a>

          <button
            className="site-menu-button"
            type="button"
            aria-label="Open navigation"
            onClick={() => setMenuOpen(true)}
          >
            <Menu size={21} />
          </button>
        </div>
      </header>

      {menuOpen ? (
        <div className="site-mobile-menu">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setMenuOpen(false)}
          >
            <X size={24} />
          </button>

          <a href="#philosophy" onClick={() => setMenuOpen(false)}>
            Why Altios
          </a>
          <a href="#services" onClick={() => setMenuOpen(false)}>
            What we build
          </a>
          <a href="#process" onClick={() => setMenuOpen(false)}>
            How we work
          </a>
          <a href="#work" onClick={() => setMenuOpen(false)}>
            Our work
          </a>
          <a href="https://portal.altiosbuilders.com">Customer Portal</a>
          <a href="https://desk.altiosbuilders.com">Employee Portal</a>
          <a
            className="site-mobile-menu__cta"
            href="https://intake.altiosbuilders.com"
          >
            Start a project
            <ArrowRight size={17} />
          </a>
        </div>
      ) : null}

      <main>
        <section className="site-hero" id="top">
          <div className="site-hero__media" aria-hidden="true">
            <div className="site-hero__architectural-grid" />
            <div className="site-hero__future-media">
              <span>Future project film</span>
            </div>
            <div className="site-hero__scrim" />
          </div>

          <div className="site-hero__content">
            <p className="site-kicker">ALTIOS BUILDERS · WESTERN NORTH CAROLINA</p>

            <h1>
              Your house should feel
              <span>like home.</span>
            </h1>

            <p className="site-hero__lede">
              Thoughtful residential remodeling built around one simple idea:
              understand what you need, communicate clearly, and build it right.
            </p>

            <div className="site-hero__actions">
              <a
                className="site-button site-button--gold"
                href="https://intake.altiosbuilders.com"
              >
                Tell us about your project
                <ArrowRight size={18} />
              </a>

              <a className="site-button site-button--quiet" href="#philosophy">
                Why Altios
                <ArrowDown size={17} />
              </a>
            </div>
          </div>

          <div className="site-hero__footer">
            <span>Renovation</span>
            <span>Remodeling</span>
            <span>Craftsmanship</span>
            <span>Communication</span>
          </div>
        </section>

        <section className="site-philosophy" id="philosophy">
          <div className="site-section-number">01</div>

          <div className="site-philosophy__intro">
            <p className="site-kicker">WHY ALTIOS</p>

            <h2>
              We have heard you.
              <br />
              <span>We listen to you.</span>
            </h2>
          </div>

          <div className="site-philosophy__copy">
            <p className="site-philosophy__lead">
              The number one complaint about construction companies is hidden
              costs and budget surprises. Poor communication is right behind
              it — and a major reason the first one happens in the first place.
            </p>

            <p className="site-source">
              NAHB, citing a 2025 Homes.com survey of more than 1,000 home
              buyers, reported hidden costs or unexpected upgrades as the top
              pain point, cited by 35% of respondents.
              {' '}
              <a
                href="https://www.nahb.org/blog/2026/06/how-builders-can-close-the-expectation-gap"
                target="_blank"
                rel="noreferrer"
              >
                Source <ArrowUpRight size={12} />
              </a>
            </p>

            <div className="site-question">
              <span>Do you ever feel like construction guys speak a totally different language than you?</span>
              <strong>We do.</strong>
            </div>

            <p>
              Contractors work in this language every day. Homeowners don’t.
              When the contractor assumes you understand what they mean, and
              you’re relying on the contractor to understand what you mean,
              your renovation story starts getting written in two different
              languages.
            </p>

            <p className="site-emphasis">That’s where problems start.</p>

            <p>
              Accurate communication matters. Transparency matters. Knowing
              what is happening, why it is happening, what it costs, and what
              comes next matters.
            </p>

            <p>
              We are here to solve your problems and educate you along the way.
            </p>

            <blockquote>
              We aren’t just here to get you a new remodel.
              <strong>
                We are here to solve the problems that are keeping you from
                feeling like you are home.
              </strong>
            </blockquote>

            <p>
              When you choose Altios Builders, you aren’t just paying for some
              contractor. You are paying for the years we spent learning —
              from our successes, our failures, and every lesson in between —
              why we do things the way we do today.
            </p>

            <div className="site-promise">
              <span>We can’t promise we won’t make mistakes.</span>
              <strong>We can promise we’ll make them right.</strong>
            </div>

            <p className="site-philosophy__finish">
              Find out what it means to be truly heard — and have the home you
              didn’t even know you needed.
            </p>
          </div>
        </section>

        <section className="site-services" id="services">
          <div className="site-section-heading">
            <div>
              <div className="site-section-number">02</div>
              <p className="site-kicker">WHAT WE BUILD</p>
            </div>

            <h2>
              The spaces you live in
              <span>should work for the way you live.</span>
            </h2>
          </div>

          <div className="site-service-list">
            <article>
              <span>01</span>
              <div>
                <h3>Kitchens</h3>
                <p>
                  Better flow, better storage, better use of the room that
                  usually works harder than any other space in the house.
                </p>
              </div>
              <ChevronRight size={20} />
            </article>

            <article>
              <span>02</span>
              <div>
                <h3>Bathrooms</h3>
                <p>
                  Functional, durable spaces designed around how you actually
                  use them every day.
                </p>
              </div>
              <ChevronRight size={20} />
            </article>

            <article>
              <span>03</span>
              <div>
                <h3>Whole-home renovations</h3>
                <p>
                  When the problem is bigger than one room, we bring the pieces
                  together into one coordinated plan.
                </p>
              </div>
              <ChevronRight size={20} />
            </article>

            <article>
              <span>04</span>
              <div>
                <h3>Custom interiors & finish work</h3>
                <p>
                  Cabinetry, built-ins, trim, stairs, and the details that make
                  a finished space feel intentional.
                </p>
              </div>
              <ChevronRight size={20} />
            </article>
          </div>
        </section>

        <section className="site-work" id="work">
          <div className="site-work__copy">
            <div className="site-section-number">03</div>
            <p className="site-kicker">OUR WORK</p>

            <h2>
              Before.
              <span>After.</span>
              Everything in between.
            </h2>

            <p>
              Project photos are coming soon. We’re building this site the same
              way we build homes — carefully.
            </p>

            <p>
              Our project gallery is still under construction, and we’ll be
              adding real before-and-after photos, finished spaces, and project
              stories as they’re ready.
            </p>

            <p className="site-work__no-stock">
              No stock photos. No borrowed work. Just Altios projects.
            </p>
          </div>

          <div className="site-work__stage" aria-label="Future project gallery">
            <div className="site-work__frame">
              <span className="site-work__label">PROJECT STORIES COMING SOON</span>

              <div className="site-work__split">
                <div>
                  <span>Before</span>
                </div>
                <div>
                  <span>After</span>
                </div>
              </div>

              <div className="site-work__timeline">
                <span />
              </div>
            </div>
          </div>
        </section>

        <section className="site-process" id="process">
          <div className="site-section-heading">
            <div>
              <div className="site-section-number">04</div>
              <p className="site-kicker">HOW WE WORK</p>
            </div>

            <h2>
              You should never have to wonder
              <span>what happens next.</span>
            </h2>
          </div>

          <div className="site-process__grid">
            <article>
              <span>01</span>
              <House size={23} />
              <h3>We listen first.</h3>
              <p>
                Before we start telling you what to build, we understand what
                isn’t working and what you need your home to do better.
              </p>
            </article>

            <article>
              <span>02</span>
              <Building2 size={23} />
              <h3>We make the process visible.</h3>
              <p>
                Decisions, selections, approvals, schedules, documents, and
                communication should not disappear into a contractor’s phone
                or notebook.
              </p>
            </article>

            <article>
              <span>03</span>
              <Check size={23} />
              <h3>We explain the why.</h3>
              <p>
                You do not need to know construction language. That is our job.
                Our job is also to make sure you understand the decision you
                are making.
              </p>
            </article>

            <article>
              <span>04</span>
              <ShieldCheck size={23} />
              <h3>We own the outcome.</h3>
              <p>
                Construction is done by people. People can make mistakes. What
                matters is whether your builder owns it and makes it right.
              </p>
            </article>
          </div>
        </section>

        <section className="site-contact">
          <div className="site-contact__copy">
            <p className="site-kicker">START A CONVERSATION</p>

            <h2>
              We know we aren’t
              <span>the right fit for everyone.</span>
            </h2>

            <p>
              We aren’t a handyman service. We also aren’t a giant office with
              layers of overhead your check has to pay for.
            </p>

            <p>
              We’re a builder with a heart for service. We want to advocate for
              you, understand what you actually need, and help you make good
              decisions about your home.
            </p>

            <p>
              We don’t advertise. Our work depends on referrals and
              relationships.
            </p>

            <p>
              But if you happened to find us here and something about the way
              we work feels like a good fit, fill out the short project intake
              form.
            </p>

            <p className="site-contact__callback">
              We’ll call you back.
            </p>

            <a
              className="site-button site-button--gold"
              href="https://intake.altiosbuilders.com"
            >
              Tell us about your project
              <ArrowRight size={18} />
            </a>
          </div>

          <div className="site-contact__links">
            <a href="https://portal.altiosbuilders.com">
              <span>Already working with us?</span>
              <strong>Customer Portal</strong>
              <ArrowUpRight size={18} />
            </a>

            <a href="https://desk.altiosbuilders.com">
              <span>Altios team</span>
              <strong>Employee Portal</strong>
              <ArrowUpRight size={18} />
            </a>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div>
          <img
            src="/logo.png"
            width="600"
            height="300"
            alt="Altios Builders"
          />
          <p>Crafting houses into homes.</p>
        </div>

        <div className="site-footer__meta">
          <span>Western North Carolina</span>
          <span>Altios Builders, LLC</span>
        </div>

        <div className="site-footer__links">
          <a href="https://intake.altiosbuilders.com">Start a project</a>
          <a href="https://portal.altiosbuilders.com">Customer Portal</a>
          <a href="https://desk.altiosbuilders.com">Employee Portal</a>
        </div>
      </footer>
    </div>
  );
}